<template>
  <div class="w-full">
    <div class="flex gap-x-2" :class="{ 'cursor-grab': freeMode }">
      <div v-if="isNavigationShown" class="flex shrink-0 items-center">
        <button
          :class="
            twMerge(
              'grid h-10 w-10 place-items-center rounded-full border-2 border-neutral-200 bg-neutral-50 focus-visible:ring focus-visible:ring-neutral-300 focus-visible:outline-none disabled:cursor-not-allowed',
              arrowClasses,
            )
          "
          @click="previousSlide"
          :disabled="isBeginning"
          test_id="carousel-button-left"
        >
          <ChevronLeftIcon :class="twMerge('size-3.5', arrowIconClasses)" />
        </button>
      </div>

      <swiper-container
        ref="swiperRef"
        init="false"
        @swiperslidechange="handleSlideChange"
        class="w-full min-w-0"
      >
        <swiper-slide v-for="(slideData, index) in slidesData" :key="index">
          <slot name="slide" :slide-data="slideData" :index="index" />
        </swiper-slide>
      </swiper-container>

      <div v-if="isNavigationShown" class="flex shrink-0 items-center">
        <button
          :class="
            twMerge(
              'grid h-10 w-10 place-items-center rounded-full border-2 border-neutral-200 bg-neutral-50 focus-visible:ring focus-visible:ring-neutral-300 focus-visible:outline-none disabled:cursor-not-allowed',
              arrowClasses,
            )
          "
          @click="nextSlide"
          :disabled="isEnd"
          test_id="carousel-button-right"
        >
          <ChevronRightIcon :class="twMerge('size-3.5', arrowIconClasses)" />
        </button>
      </div>
    </div>

    <div v-if="isPaginationEnabled" class="mt-4 flex w-full justify-center">
      <div class="swiper-pagination" />
    </div>
  </div>
</template>

<script setup lang="ts" generic="T">
import { register } from 'swiper/element'
import ChevronLeftIcon from '~icons/lucide/chevron-left'
import ChevronRightIcon from '~icons/lucide/chevron-right'
import { computed, onMounted, ref, watch } from 'vue'
import { twMerge } from 'tailwind-merge'

register()

type Breakpoint = {
  slidesPerView: number
  spaceBetween?: number
  freeMode?: boolean
  grabCursor?: boolean
}

type Breakpoints = {
  [key: number]: Breakpoint
}

type SwiperOptions = {
  slidesPerView: number
  spaceBetween: number
  freeMode: boolean
  grabCursor: boolean
  breakpoints: Breakpoints
  pagination: {
    el: string
    clickable: boolean
    dynamicBullets: boolean
  }
}

type SwiperInstance = {
  slidePrev: (speed?: number) => void
  slideNext: (speed?: number) => void
  slideTo: (index: number, speed?: number) => void
  isBeginning: boolean
  isEnd: boolean
}

type SwiperElement = {
  swiper: SwiperInstance
  initialize: () => void
} & HTMLElement

type Props<T> = {
  slidesPerView?: number
  spaceBetween?: number
  freeMode?: boolean
  grabCursor?: boolean
  breakpoints?: Breakpoints
  isNavigationEnabled?: boolean
  slidesData: T[]
  isHidingNavigationDisabled?: boolean
  arrowClasses?: string | string[]
  arrowIconClasses?: string | string[]
  isPaginationEnabled?: boolean
  currentSlideIndex?: number
}

const props = withDefaults(defineProps<Props<T>>(), {
  slidesPerView: 1,
  spaceBetween: 20,
  freeMode: false,
  grabCursor: false,
  breakpoints: () => ({}),
  isNavigationEnabled: true,
  isHidingNavigationDisabled: false,
  arrowClasses: '',
  arrowIconClasses: '',
  isPaginationEnabled: false,
  currentSlideIndex: 0,
})

const swiperRef = ref<SwiperElement | null>(null)
const slidesLength = ref(0)
const transitionSpeedMs = ref(500)
const swiperOption = ref<SwiperOptions>({
  slidesPerView: props.slidesPerView,
  spaceBetween: props.spaceBetween,
  freeMode: props.freeMode,
  grabCursor: props.grabCursor,
  breakpoints: props.breakpoints,
  pagination: {
    el: '.swiper-pagination',
    clickable: true,
    dynamicBullets: false,
  },
})
const isBeginning = ref(true)
const isEnd = ref(false)

const isNavigationShown = computed(() => {
  // By default, navigation is hidden on mobile phones
  if (typeof window === 'undefined') {
    return false
  }
  if (!props.isHidingNavigationDisabled && window.innerWidth < 576) {
    return false
  }

  const isMoreDataThanSlidesPerView =
    props.slidesData.length > slidesLength.value
  return props.isNavigationEnabled && isMoreDataThanSlidesPerView
})

const handleSlideChange = () => {
  updateNavigationStatus()
}

const previousSlide = () => {
  if (swiperRef.value?.swiper) {
    swiperRef.value.swiper.slidePrev(transitionSpeedMs.value)
  }
}

const nextSlide = () => {
  if (swiperRef.value?.swiper) {
    swiperRef.value.swiper.slideNext(transitionSpeedMs.value)
  }
}

const updateNavigationStatus = () => {
  if (swiperRef.value?.swiper) {
    isBeginning.value = swiperRef.value.swiper.isBeginning
    isEnd.value = swiperRef.value.swiper.isEnd
  }
}

const updateActiveSlidesPerView = () => {
  const breakpoints = Object.keys(props.breakpoints).map(parseFloat)
  breakpoints
    .sort((a, b) => a - b)
    .forEach((breakpoint) => {
      if (window.innerWidth > breakpoint) {
        slidesLength.value = props.breakpoints[breakpoint].slidesPerView
      }
    })
}

watch(
  () => props.currentSlideIndex,
  (value) => {
    if (swiperRef.value?.swiper) {
      swiperRef.value.swiper.slideTo(value, transitionSpeedMs.value)
    }
  },
)

onMounted(() => {
  if (swiperRef.value) {
    Object.assign(swiperRef.value, swiperOption.value)
    swiperRef.value.initialize()
    updateActiveSlidesPerView()
    updateNavigationStatus()
  }
})

defineExpose({
  isBeginning,
  isEnd,
  previousSlide,
  nextSlide,
})
</script>

<style>
@reference "#tailwind-theme";

.swiper-container {
  flex-grow: 1;
}

.swiper-container {
  z-index: 0 !important;
}

.swiper-pagination {
  @apply flex items-center;
}

.swiper-pagination-bullet {
  @apply m-1.5 bg-neutral-200 opacity-100;
}

.swiper-pagination-bullet-active {
  @apply h-3 w-3 bg-neutral-400;
}
</style>
